import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import React from 'react';

import { StatusBadge } from '../../../../../components/atoms/StatusBadge';
import { sanitizeElement } from '../../../../../utils/common';
import { tidyNoteText } from '../../../../../utils/note-text-tidy';

type LogRecord = Record<string, unknown>;

/** Truncate a timestamp to the minute for grouping (ignore seconds/ms differences). */
const toMinuteKey = (raw: unknown): string => {
  if (!raw || typeof raw !== 'string') return '';
  try {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return String(raw);
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}-${d.getHours()}-${d.getMinutes()}`;
  } catch {
    return String(raw);
  }
};

const logGroupKey = (log: LogRecord): string => {
  const time = toMinuteKey(log.time ?? log.createdAt ?? log.dateTime);
  const user = String(log.userName ?? log.user ?? log.createdBy ?? '');
  return `${time}_${user}`;
};

/**
 * Group interaction-event logs with their triggering log (same minute + user).
 * Returns logs with `_attachedInteractions` for combined rendering.
 */
export const groupInteractionsWithTrigger = (logs: LogRecord[]): LogRecord[] => {
  const interactionMap = new Map<string, LogRecord[]>();
  const nonInteractionLogs: LogRecord[] = [];
  for (const log of logs) {
    const code = String(log.logTypeCode ?? '').toLowerCase();
    if (code === 'interaction-events') {
      const key = logGroupKey(log);
      if (!interactionMap.has(key)) interactionMap.set(key, []);
      interactionMap.get(key)!.push(log);
    } else {
      nonInteractionLogs.push(log);
    }
  }
  const result = nonInteractionLogs.map((log) => {
    const key = logGroupKey(log);
    const attached = interactionMap.get(key);
    if (attached) {
      interactionMap.delete(key);
      return { ...log, _attachedInteractions: attached };
    }
    return log;
  });
  for (const orphans of interactionMap.values()) {
    result.push(...orphans);
  }
  return result;
};

/** Render one segment of note text, keeping the existing HTML handling. */
const NoteTextSegment: React.FC<{ text: string }> = ({ text }) => {
  // Only render as HTML if it actually contains tags
  if (/<[a-z][\s\S]*>/i.test(text)) {
    const sanitized = sanitizeElement(text);
    return (
      <Box
        component="span"
        sx={{
          '& p': { m: 0 },
          '& br': { display: 'block', content: '""', mt: 0.25 },
          fontSize: 'inherit',
        }}
        dangerouslySetInnerHTML={{ __html: sanitized }}
      />
    );
  }

  return (
    <Box component="span" sx={{ whiteSpace: 'pre-wrap' }}>
      {text}
    </Box>
  );
};

/**
 * Safely render notes that may contain HTML, using DOMPurify.
 *
 * Notes written before interaction answers were formatted properly can contain a
 * raw JSON payload or a whole base64 signature. `tidyNoteText` collapses those at
 * render time — the stored note is never modified — so historical records read
 * like the new ones.
 */
export const SafeNotes: React.FC<{ html: string }> = ({ html }) => {
  if (!html || html === '-') {
    return <>{html || '-'}</>;
  }

  const segments = tidyNoteText(html);

  return (
    <>
      {segments.map((segment, i) =>
        segment.kind === 'image' ? (
          <Box
            key={i}
            component="img"
            src={segment.src}
            alt="Signature"
            sx={{
              display: 'block',
              my: 0.25,
              maxWidth: 160,
              maxHeight: 60,
              objectFit: 'contain',
              border: '1px solid',
              borderColor: 'v4.border',
              borderRadius: 0.5,
              backgroundColor: 'common.white',
            }}
          />
        ) : (
          <NoteTextSegment key={i} text={segment.text} />
        )
      )}
    </>
  );
};

/**
 * Render a status-change history entry: a bold "Status changed:" prefix with the
 * from→to status badges, then a bold "Notes:" line (defaults to "N/A" when empty).
 * Status-change only — other log types render via SafeNotes unchanged.
 */
export const StatusChangeNote: React.FC<{
  fromStatus?: string;
  toStatus?: unknown;
  notes: string;
}> = ({ fromStatus, toStatus, notes }) => {
  const from = fromStatus ?? '';
  const to = String(toStatus ?? '');
  const noteText = notes && notes !== '-' ? notes : 'N/A';
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, mb: 0.5 }}>
        <Typography component="span" sx={{ fontSize: '12px', fontWeight: 600 }}>
          Status changed:
        </Typography>
        {from ? (
          <StatusBadge status={from} size="small" />
        ) : (
          <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>—</Typography>
        )}
        <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>→</Typography>
        <StatusBadge status={to} size="small" />
      </Box>
      <Box>
        <Typography component="span" sx={{ fontSize: '12px', fontWeight: 600 }}>
          Notes:{' '}
        </Typography>
        <SafeNotes html={noteText} />
      </Box>
    </>
  );
};
